import { AppSidebar } from '@/components/app-sidebar'
import { BackButton } from '@/components/back-button'
import { AssistantWidget } from '@/components/dashboard/common/assistant/assistant-widget'
import AddBugReport from '@/components/dashboard/organisation/bug-report/add-bug-report'
import { FocusModeExitButton, FocusModeToggle } from '@/components/focus-mode-toggle'
import Modal from '@/components/modal'
import { Notification } from '@/components/notification'
import OnboardNotification from '@/components/onboard-notification'
import { OrgSelect } from '@/components/org-select'
import { ThemeToggle } from '@/components/theme-toggle'
import { TipsModal } from '@/components/tips-modal'
import { AutoBreadcrumb } from '@/components/ui/auto-breadcrumb'
import { Button } from '@/components/ui/button'
import { CommandMenu } from '@/components/ui/command-menu'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { UserGuidePlayer } from '@/components/user-guide-player'
import { endPoint } from '@/constant/end-point'
import { Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import { useFocusMode } from '@/hooks/use-focus-mode'
import { useNavigationTrail } from '@/hooks/use-navigation-trail'
import type { IUserOrganisations } from '@/interface/auth.interface'
import type { IDefaultOrganisation } from '@/interface/user-preference.interface'
import { onMessageListener, requestForToken } from '@/utils/firebase'
import { isOrgRole } from '@/utils/role-check.utils'
import { useUser } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bug, BugIcon, Settings } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'

export const DashboardTemplate = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  useNavigationTrail()
  const queryClient = useQueryClient()
  const isOnboardingPage = pathname.startsWith('/on-boarding')
  const { user, isLoaded } = useUser()
  const { isFocusMode } = useFocusMode()
  const [bugReportOpen, setBugReportOpen] = useState(false)
  const { activeOrg, setActiveOrg } = useActiveOrg()
  const { getOne: getDefaultOrganisation } = useAPI<IDefaultOrganisation>()
  const { getOne: getOrganisationAndPermissions } = useAPI<IUserOrganisations>()

  const { data: organisationAndPermissions } = useQuery({
    queryKey: ['organisationAndPermissions'],
    queryFn: async () => {
      const response = await getOrganisationAndPermissions(endPoint?.organisationAndPermissions, {})
      if (!response?.status) throw new Error(response?.message)

      return {
        data: response?.data,
      }
    },
    enabled: isLoaded,
  })

  const { data: defaultOrganisation } = useQuery({
    queryKey: ['defaultOrganisation'],
    queryFn: async () => {
      const response = await getDefaultOrganisation(endPoint?.defaultOrganisation, {})
      if (!response?.status) throw new Error(response?.message)

      return {
        data: response?.data,
      }
    },
    enabled: isLoaded,
  })

  useEffect(() => {
    if (defaultOrganisation && organisationAndPermissions?.data?.organisations) {
      const foundTeam = organisationAndPermissions?.data?.organisations.find(
        (organisation) => organisation.organisation.id === defaultOrganisation.data?.defaultOrganisationId
      )
      if (foundTeam)
        setActiveOrg({
          id: foundTeam.organisation.id,
          name: foundTeam.organisation.name,
          logo: foundTeam.organisation.logo,
          role: foundTeam.role,
          featurePermissions: foundTeam.featurePermissions,
          isOnboardingCompleted: defaultOrganisation.data?.isOnboardingCompleted,
          parentId: foundTeam.organisation.parentId,
        })
    }
  }, [defaultOrganisation, organisationAndPermissions?.data?.organisations, setActiveOrg])

  useEffect(() => {
    // 1. Request permission and get token
    requestForToken()
    // 2. Listen for foreground messages
    onMessageListener()
  }, [user])

  return (
    <div className='bg-background text-foreground min-h-screen flex flex-col items-center justify-center'>
      {/* Focus Mode Exit Button - Floating */}
      <FocusModeExitButton />

      <SidebarProvider>
        {/* Hide sidebar in focus mode */}
        {!isFocusMode && (
          <AppSidebar user={user} organisations={organisationAndPermissions?.data?.organisations || null} />
        )}
        <SidebarInset className={isFocusMode ? 'ml-0' : ''}>
          {/* Hide header in focus mode */}
          {!isFocusMode && (
            <header className='@container/app-header flex h-16 w-full min-w-0 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12'>
              <div className='flex min-w-0 items-center gap-2 px-2 md:px-4'>
                <SidebarTrigger id='nav-trigger' className='-ml-1' />
                <BackButton />
                <div className='hidden min-w-0 items-center gap-2 @3xl/app-header:flex'>
                  <Separator orientation='vertical' className='mr-2 data-[orientation=vertical]:h-4' />
                  <AutoBreadcrumb />
                </div>
              </div>
              <div className='ml-auto flex shrink-0 items-center gap-1 px-2 md:gap-2 md:px-4'>
                {activeOrg?.id && activeOrg?.role && isOrgRole(activeOrg?.role) && <AssistantWidget />}
                {activeOrg?.role && isOrgRole(activeOrg?.role) ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant='outline'
                        size='icon'
                        className='cursor-pointer'
                        onClick={() => setBugReportOpen(true)}
                      >
                        <Bug className='h-[1.2rem] w-[1.2rem]' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Bug & Feature Request</p>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                <Notification />
                {activeOrg?.role && isOrgRole(activeOrg?.role) && activeOrg?.role === Role.ORG_ADMIN ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (activeOrg?.id) navigate(`/on-boarding/${activeOrg?.id}`)
                        }}
                        variant='outline'
                        size='icon'
                        className='cursor-pointer'
                      >
                        <Settings className='h-[1.2rem] w-[1.2rem]' />
                        <span className='sr-only'>Settings</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Organisation Onboarding</p>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
                <div className='lg:block hidden'>
                  <FocusModeToggle />
                </div>
                <ThemeToggle />
              </div>
            </header>
          )}
          <div
            className={`min-w-0 max-w-full p-4 rounded-sm overflow-y-auto overflow-x-clip bg-sidebar text-sidebar-foreground transition-all duration-300 ${
              isFocusMode ? 'h-screen m-0 rounded-none' : 'h-[calc(100vh-78px)] mx-4'
            }`}
          >
            {organisationAndPermissions?.data?.organisations &&
            organisationAndPermissions?.data?.organisations?.length > 0 ? (
              activeOrg?.id ? (
                <>
                  {activeOrg?.role && isOrgRole(activeOrg?.role) && !isOnboardingPage && <OnboardNotification />}
                  <Outlet />
                  <UserGuidePlayer />
                </>
              ) : (
                <div>
                  <FullPageSkeleton />
                  <OrgSelect organisations={organisationAndPermissions?.data?.organisations || null} />
                </div>
              )
            ) : (
              <FullPageSkeleton />
            )}
            <CommandMenu user={user} organisations={organisationAndPermissions?.data?.organisations || null} />
          </div>
        </SidebarInset>
      </SidebarProvider>
      <TipsModal />
      <Modal
        open={bugReportOpen}
        setOpen={setBugReportOpen}
        title='Bug & Feature Request'
        rightJSX={
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              setBugReportOpen(false)
              navigate('/bug-report')
            }}
          >
            <BugIcon className='h-4 w-4' />
            View My Reports
          </Button>
        }
      >
        <AddBugReport
          addOpen={bugReportOpen}
          setAddOpen={setBugReportOpen}
          modalMode
          onAddSuccess={() => void queryClient.invalidateQueries({ queryKey: [endPoint.bugReport], exact: false })}
        />
      </Modal>
    </div>
  )
}
export default DashboardTemplate
